import { Head, Link, usePage } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import type { Engineer, ProofModule, ProofModuleGitHub, ProofModuleResearch, ProofModuleModels } from '@/types/engineer'
import type { SharedPageProps } from '@/types/shared'
import { EvidenceCoverageStrip } from '@/components/Shared/EvidenceCoverageStrip'
import { FacetCard } from '@/components/Shared/FacetCard'
import { DepthLevelMeter } from '@/components/Shared/DepthLevelMeter'
import { CandidateNav } from '@/components/Shared/CandidateNav'
import { PublicHeader } from '@/components/Shared/PublicHeader'
import { GhostSection } from '@/components/Shared/GhostSection'
import { VerificationMark } from '@/components/Shared/VerificationMark'
import { AssessmentCtaBanner } from '@/components/Shared/AssessmentCtaBanner'

interface EngineerShowProps {
  engineer: Engineer
  isOwner: boolean
}

function isGitHub(m: ProofModule): m is ProofModuleGitHub {
  return m.source === 'github'
}
function isResearch(m: ProofModule): m is ProofModuleResearch {
  return m.source === 'arxiv'
}
function isModels(m: ProofModule): m is ProofModuleModels {
  return m.source === 'huggingface'
}

function yearOf(date: string | null): string | null {
  return date ? date.slice(0, 4) : null
}

function dateRange(start: string | null, end: string | null): string | null {
  const from = yearOf(start)
  if (!from) {
    return null
  }
  return `${from} — ${yearOf(end) ?? 'Present'}`
}

function formatSyncedDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const SYNC_NOTE_MESSAGES: Record<'syncing' | 'failed' | 'partial', string> = {
  syncing: 'sync in progress',
  failed: 'last sync failed',
  partial: "some repositories didn't sync",
}

const DAY_OFFSETS = [ 0, 1, 2, 3, 4, 5, 6 ] as const

function contributionCellClass(count: number): string {
  if (count <= 0) {
    return 'bg-muted'
  }
  if (count <= 2) {
    return 'bg-primary/25'
  }
  if (count <= 5) {
    return 'bg-primary/50'
  }
  if (count <= 9) {
    return 'bg-primary/75'
  }
  return 'bg-primary'
}

const GITHUB_GHOST_META: Record<'pending' | 'syncing' | 'failed', { title: string, hint: string }> = {
  pending: {
    title: 'GitHub proof',
    hint: 'Connect your agent and consent repositories — your verified GitHub proof lands here.',
  },
  syncing: {
    title: 'GitHub proof',
    hint: 'Syncing your GitHub data — refresh in a moment.',
  },
  failed: {
    title: 'GitHub proof',
    hint: 'Last sync failed — ask your agent to re-submit repository consent.',
  },
}

const HUGGINGFACE_GHOST_META: Record<'pending' | 'syncing' | 'failed', { title: string, hint: string }> = {
  pending: {
    title: 'Hugging Face proof',
    hint: 'Connect your agent and consent models — your Hugging Face proof lands here.',
  },
  syncing: {
    title: 'Hugging Face proof',
    hint: 'Syncing your Hugging Face data — refresh in a moment.',
  },
  failed: {
    title: 'Hugging Face proof',
    hint: 'Last sync failed — ask your agent to re-submit model consent.',
  },
}

function EmptyProfile() {
  return (
    <main className="mx-auto max-w-[560px] px-6 py-20 text-center">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        Profile · version 0
      </p>
      <h1 className="mb-3 font-heading text-[28px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
        This profile isn&apos;t published yet
      </h1>
      <p className="font-sans text-[15px] leading-[1.5] text-muted-foreground">
        The engineer has not imported their profile yet — check back soon.
      </p>
    </main>
  )
}

export default function EngineerShow({ engineer, isOwner }: EngineerShowProps) {
  const { currentPerson } = usePage<SharedPageProps>().props

  const proofModules = engineer.proofModules ?? []
  const github = proofModules.find(isGitHub)
  const research = proofModules.find(isResearch)
  const models = proofModules.find(isModels)

  const skillSignals = engineer.skillSignals ?? []
  const verifiedSignals = skillSignals.filter((s) => s.verificationStatus !== 'self_attested')
  const selfAttestedSignals = skillSignals.filter((s) => s.verificationStatus === 'self_attested')
  const facets = engineer.facets ?? []
  const firstName = engineer.name?.split(' ')[0]

  return (
    <>
      <Head title={`${engineer.name ?? (isOwner ? 'Your profile' : 'Engineer profile')} — Hyrin`} />

      <div className="min-h-screen bg-background">
        {currentPerson ? <CandidateNav /> : (
          <PublicHeader>
            <Link
              href="/sign-in/new"
              className="font-sans text-[14px] text-muted-foreground transition-colors hover:text-foreground"
            >
              Sign in
            </Link>
          </PublicHeader>
        )}

        {!engineer.profilePopulated && !isOwner && <EmptyProfile />}

        {(engineer.profilePopulated || isOwner) && (
          <div className="flex flex-col lg:flex-row">
            {/* Sidebar */}
            <aside className="border-b border-border bg-sidebar px-6 py-8 lg:sticky lg:top-0 lg:h-screen lg:w-[312px] lg:shrink-0 lg:overflow-y-auto lg:border-b-0 lg:border-r lg:px-[30px] lg:py-9">
              {engineer.avatarUrl ? (
                <img
                  src={engineer.avatarUrl}
                  alt={engineer.name ?? 'Avatar'}
                  className="mb-5 size-[84px] rounded-[20px] object-cover"
                />
              ) : (
                <div className="mb-5 flex size-[84px] items-center justify-center rounded-[20px] bg-primary font-heading text-[32px] font-bold text-primary-foreground">
                  {engineer.initials}
                </div>
              )}

              <h1 className="mb-2 font-heading text-[28px] font-bold leading-[1.05] tracking-[-0.02em] text-foreground">
                {engineer.name ?? engineer.id}
              </h1>
              {engineer.headline && (
                <p className="mb-[6px] font-sans text-[16px] leading-[1.45] text-muted-foreground">
                  {engineer.headline}
                </p>
              )}
              {engineer.location && (
                <p className="mb-[18px] font-sans text-[14px] text-muted-foreground">
                  {engineer.location}
                </p>
              )}

              {engineer.openToWork && (
                <span className="mb-6 inline-flex items-center gap-[6px] rounded-full border border-sidebar-border bg-background px-3 py-[7px] font-sans text-[11px] font-semibold text-primary">
                  <span className="size-[6px] rounded-full bg-primary" />
                  Open to work
                </span>
              )}

              {/* Builder persona — dormant until scoring backs it */}
              {engineer.builderPersona && (
                <div className="mb-5 rounded-[14px] border border-sidebar-border bg-background p-[18px]">
                  <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                    Builder persona
                  </p>
                  <p className="font-heading text-[22px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
                    {engineer.builderPersona.primaryBuilderType}
                  </p>
                  <span className="mt-2 inline-flex rounded-full bg-secondary px-[10px] py-[4px] font-mono text-[10px] uppercase tracking-[0.08em] text-primary">
                    {engineer.builderPersona.executionStyle}
                  </span>
                  <p className="mt-3 font-sans text-[13px] leading-[1.5] text-muted-foreground">
                    {engineer.builderPersona.personaSummary}
                  </p>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                    confidence · {engineer.builderPersona.confidenceLevel}
                  </p>
                </div>
              )}

              {/* Evidence coverage — dormant until facets are backed */}
              {facets.length > 0 && (
                <div className="mb-5 rounded-[14px] border border-sidebar-border bg-background p-[18px]">
                  <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Evidence coverage
                  </p>
                  <EvidenceCoverageStrip facets={facets} />
                </div>
              )}

              {/* Links */}
              {engineer.links.length > 0 && (
                <>
                  <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Links
                  </p>
                  <ul className="mb-6 flex flex-col gap-[9px]">
                    {engineer.links.map((link) => (
                      <li key={link.type} className="font-sans text-[14px]">
                        {link.url ? (
                          <a href={link.url} target="_blank" rel="noreferrer" className="text-foreground transition-colors hover:text-primary">
                            {link.handle ?? link.url}
                            <span className="ml-2 font-mono text-[11px] uppercase text-muted-foreground">{link.type}</span>
                          </a>
                        ) : (
                          <span className="text-foreground">
                            {link.handle ?? link.type}
                            <span className="ml-2 font-mono text-[11px] uppercase text-muted-foreground">{link.type}</span>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {isOwner && engineer.links.length === 0 && (
                <GhostSection
                  variant="actionable"
                  compact
                  onRail
                  title="Links"
                  hint="GitHub, personal site and more — added by your agent."
                  className="mb-6"
                />
              )}

              {!isOwner && firstName && (
                <button className="h-[46px] w-full rounded-[11px] bg-foreground font-sans text-[15px] font-semibold text-background transition-opacity hover:opacity-90">
                  Contact {firstName}
                </button>
              )}
            </aside>

            {/* Main */}
            <main className="flex-1 px-5 py-8 sm:px-8 lg:px-10 lg:py-9">
              {isOwner && !engineer.profilePopulated && (
                <div className="mb-[30px] flex flex-col gap-4 rounded-[14px] bg-foreground px-6 py-5 sm:flex-row sm:items-center sm:gap-6">
                  <div className="flex-1">
                    <p className="mb-1 font-heading text-[17px] font-bold text-background">
                      Your profile is empty — here&apos;s what it becomes.
                    </p>
                    <p className="font-sans text-[13.5px] leading-[1.45] text-background/70">
                      Connect your AI agent to import your résumé, GitHub, and other sources — it
                      builds and updates your profile for you.
                    </p>
                  </div>
                  <Link
                    href="/connect"
                    className="inline-flex h-[42px] shrink-0 items-center justify-center rounded-[9px] bg-background px-[18px] font-sans text-[14px] font-semibold text-foreground"
                  >
                    Connect your agent
                  </Link>
                </div>
              )}
              {engineer.bio && (
                <p className="mb-[30px] max-w-[640px] font-heading text-[20px] leading-[1.5] tracking-[-0.01em] text-foreground">
                  {engineer.bio}
                </p>
              )}
              {isOwner && !engineer.bio && (
                <GhostSection
                  variant="actionable"
                  title="About"
                  hint="A short intro — imported from your résumé or written by your agent."
                  className="mb-[30px] max-w-[640px]"
                />
              )}

              {/* Candidate facets — dormant until scoring backs them */}
              {facets.length > 0 && (
                <>
                  <h2 className="mb-1 font-heading text-[18px] font-bold text-foreground">
                    Candidate facets
                  </h2>
                  <p className="mb-[18px] font-sans text-[14px] leading-[1.4] text-muted-foreground">
                    Observable qualities, each backed by evidence and its state — never a single score.
                  </p>
                  <div className="mb-[34px] grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {facets.map((facet) => (
                      <FacetCard key={facet.facetType} facet={facet} />
                    ))}
                  </div>
                </>
              )}

              {/* Skills — captured from the profile */}
              {engineer.skills.length > 0 && (
                <>
                  <h2 className="mb-1 font-heading text-[18px] font-bold text-foreground">Skills</h2>
                  <p className="mb-[14px] font-sans text-[14px] leading-[1.4] text-muted-foreground">
                    Imported from your résumé and sources by your agent.
                  </p>
                  <div className="mb-[34px] flex flex-wrap gap-[10px]">
                    {engineer.skills.map((skill) => (
                      <span
                        key={skill.name}
                        className="inline-flex items-center rounded-[9px] border border-border bg-card px-[13px] py-[10px] font-sans text-[15px] text-foreground"
                      >
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </>
              )}
              {isOwner && engineer.skills.length === 0 && (
                <GhostSection
                  variant="actionable"
                  title="Skills"
                  hint="Imported from your résumé and sources by your agent."
                  className="mb-[34px]"
                />
              )}

              {/* Work experience */}
              {engineer.workExperiences.length > 0 && (
                <>
                  <h2 className="mb-[14px] font-heading text-[18px] font-bold text-foreground">
                    Work experience
                  </h2>
                  <div className="mb-[34px] flex flex-col gap-4">
                    {engineer.workExperiences.map((experience) => {
                      const range = dateRange(experience.startDate, experience.endDate)
                      return (
                        <div key={experience.id} className="rounded-[13px] border border-border bg-card p-[18px]">
                          <div className="flex items-baseline justify-between gap-4">
                            <p className="font-sans text-[16px] font-semibold text-foreground">
                              {experience.title} · {experience.company}
                            </p>
                            {range && (
                              <span className="shrink-0 font-mono text-[12px] text-muted-foreground">
                                {range}
                              </span>
                            )}
                          </div>
                          {experience.location && (
                            <p className="mt-[3px] font-sans text-[13px] text-muted-foreground">{experience.location}</p>
                          )}
                          {experience.description && (
                            <p className="mt-2 font-sans text-[14px] leading-[1.5] text-muted-foreground">
                              {experience.description}
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
              {isOwner && engineer.workExperiences.length === 0 && (
                <GhostSection
                  variant="actionable"
                  title="Work experience"
                  hint="Roles and impact, imported from your résumé."
                  className="mb-[34px]"
                />
              )}

              {/* Education */}
              {engineer.educations.length > 0 && (
                <>
                  <h2 className="mb-[14px] font-heading text-[18px] font-bold text-foreground">
                    Education
                  </h2>
                  <div className="mb-[34px] flex flex-col gap-4">
                    {engineer.educations.map((education) => {
                      const range = dateRange(education.startDate, education.endDate)
                      return (
                        <div key={education.id} className="rounded-[13px] border border-border bg-card p-[18px]">
                          <div className="flex items-baseline justify-between gap-4">
                            <p className="font-sans text-[16px] font-semibold text-foreground">
                              {education.institution}
                            </p>
                            {range && (
                              <span className="shrink-0 font-mono text-[12px] text-muted-foreground">
                                {range}
                              </span>
                            )}
                          </div>
                          {(education.degree ?? education.fieldOfStudy) && (
                            <p className="mt-[3px] font-sans text-[14px] text-muted-foreground">
                              {[education.degree, education.fieldOfStudy].filter(Boolean).join(' · ')}
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
              {isOwner && engineer.educations.length === 0 && (
                <GhostSection
                  variant="actionable"
                  title="Education"
                  hint="Degrees and programs, imported from your résumé."
                  className="mb-[34px]"
                />
              )}

              {/* Books */}
              {engineer.books.length > 0 && (
                <>
                  <h2 className="mb-[14px] font-heading text-[18px] font-bold text-foreground">
                    Bookshelf
                  </h2>
                  <div className="mb-[34px] grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {engineer.books.map((book) => (
                      <div key={book.id} className="rounded-[13px] border border-border bg-card p-[18px]">
                        <p className="font-sans text-[16px] font-semibold text-foreground">{book.title}</p>
                        <p className="mt-[3px] font-sans text-[13px] text-muted-foreground">{book.author}</p>
                        {book.description && (
                          <p className="mt-2 font-sans text-[13px] leading-[1.5] text-muted-foreground">
                            {book.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {isOwner && engineer.books.length === 0 && (
                <GhostSection
                  variant="actionable"
                  title="Bookshelf"
                  hint="Books that shaped how you build."
                  className="mb-[34px]"
                />
              )}
              {isOwner && facets.length === 0 && skillSignals.length === 0 && (
                <GhostSection
                  variant="comingSoon"
                  title="Verified skills & evidence"
                  hint="Skill signals with evidence behind them — appears once Hyrin scoring ships."
                  className="mb-[34px]"
                />
              )}

              {/* Skill signals — dormant until verification backs them */}
              {verifiedSignals.length > 0 && (
                <>
                  <h2 className="mb-1 font-heading text-[18px] font-bold text-foreground">
                    Skill signals
                  </h2>
                  <p className="mb-[18px] font-sans text-[14px] leading-[1.4] text-muted-foreground">
                    Depth read from evidence on a four-step scale — Exposure, Working, Production, Expert.
                  </p>
                  <div className="mb-[34px] grid grid-cols-1 gap-x-7 gap-y-5 sm:grid-cols-2">
                    {verifiedSignals.map((signal) => (
                      <div key={signal.skillName}>
                        <div className="flex justify-between">
                          <span className="font-sans text-[16px] font-semibold text-foreground">
                            {signal.skillName}
                          </span>
                          <VerificationMark status={signal.verificationStatus} />
                        </div>
                        {signal.depthLevel && <DepthLevelMeter level={signal.depthLevel} className="mt-2" />}
                        {signal.evidence && (
                          <p className="mt-[7px] font-sans text-[13px] text-muted-foreground">
                            {signal.evidence}
                            {signal.recency && ` · ${signal.recency}`}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Self-attested — dormant until verification backs it */}
              {selfAttestedSignals.length > 0 && (
                <>
                  <h2 className="mb-1 font-heading text-[18px] font-bold text-foreground">
                    Self-attested
                  </h2>
                  <p className="mb-[14px] max-w-[560px] font-sans text-[14px] leading-[1.4] text-muted-foreground">
                    From {firstName ?? 'the'} résumé — no linked source yet. Shown unverified until a
                    connected source backs them up.
                  </p>
                  <div className="mb-[34px] flex flex-wrap items-center gap-[10px]">
                    {selfAttestedSignals.map((signal) => (
                      <span
                        key={signal.skillName}
                        className="inline-flex items-center gap-2 rounded-[9px] border border-dashed border-border px-[13px] py-[10px] font-sans text-[16px] text-muted-foreground"
                      >
                        <span className="size-[9px] rounded-full border-[1.5px] border-border" />
                        {signal.skillName}
                      </span>
                    ))}
                    <button className="h-[38px] rounded-[9px] border border-primary bg-background px-[14px] font-mono text-[13px] font-semibold text-primary">
                      Connect a source to verify
                    </button>
                  </div>
                </>
              )}

              {/* Assessment CTA — owner-only, server-decided via assessmentCta */}
              <AssessmentCtaBanner cta={engineer.assessmentCta ?? null} className="mb-6" />

              {/* Proof modules grid — dormant until live source ingestion backs it */}
              {(proofModules.length > 0 || engineer.githubGhost || engineer.huggingfaceGhost) && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.5fr_1fr]">
                  {/* GitHub — spans 2 rows */}
                  {github && (
                    <div className="rounded-[13px] border border-border bg-card p-[22px] sm:row-span-2">
                      <div className="mb-[18px] flex items-center justify-between">
                        <div className="flex items-center gap-[9px]">
                          <span className="font-heading text-[18px] font-bold text-foreground">
                            GitHub
                          </span>
                          <span className="font-mono text-[14px] text-muted-foreground">
                            {github.handle}
                          </span>
                        </div>
                        <span className="font-mono text-[14px] text-primary">✓ verified</span>
                      </div>
                      {(github.lastSyncedAt ?? github.syncNote) && (
                        <div className="mb-[14px] flex flex-wrap items-center gap-x-3 gap-y-1">
                          {github.lastSyncedAt && (
                            <p className="font-mono text-[11px] text-muted-foreground">
                              last synced {formatSyncedDate(github.lastSyncedAt)}
                            </p>
                          )}
                          {github.syncNote && (
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {SYNC_NOTE_MESSAGES[github.syncNote]}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="mb-5 flex gap-6">
                        <div>
                          <p className="font-heading text-[22px] font-bold text-foreground">
                            {github.followers.toLocaleString()}
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                            followers
                          </p>
                        </div>
                        <div>
                          <p className="font-heading text-[22px] font-bold text-foreground">
                            {github.starsEarned.toLocaleString()}
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                            stars earned
                          </p>
                        </div>
                        <div>
                          <p className="font-heading text-[22px] font-bold text-foreground">
                            {github.publicRepos}
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                            public repos
                          </p>
                        </div>
                      </div>
                      {github.languages.length > 0 && (
                        <div className="mb-5 flex flex-wrap gap-[8px]">
                          {github.languages.map((language) => (
                            <span
                              key={language.name}
                              className="inline-flex items-center gap-[6px] rounded-full border border-border bg-background px-[10px] py-[5px] font-mono text-[11px] text-muted-foreground"
                            >
                              {language.name} ×{language.count}
                            </span>
                          ))}
                        </div>
                      )}
                      {github.contributionCalendar && (
                        <div className="mb-5">
                          <div className="overflow-x-auto">
                            <div className="grid w-max auto-cols-max grid-flow-col gap-[3px]">
                              {github.contributionCalendar.weeks.map((week) => (
                                <div key={week.weekStart} className="flex flex-col gap-[3px]">
                                  {DAY_OFFSETS.map((dayOffset) => (
                                    <div
                                      key={`${week.weekStart}-${dayOffset}`}
                                      title={`${week.days[dayOffset] ?? 0} contributions`}
                                      className={cn('size-[7px] rounded-[2px]', contributionCellClass(week.days[dayOffset] ?? 0))}
                                    />
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                          <p className="mt-2 font-mono text-[11px] text-muted-foreground">
                            contributions · last 21 weeks
                          </p>
                        </div>
                      )}
                      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                        Top repositories
                      </p>
                      <div className="flex flex-col gap-[10px]">
                        {github.topRepositories.map((repo) => (
                          <div
                            key={repo.fullName}
                            className="flex items-center gap-3 rounded-[9px] border border-border p-3"
                          >
                            <div className="flex-1">
                              <p className="font-mono text-[16px] font-semibold text-foreground">
                                {repo.name}
                              </p>
                              {repo.description && (
                                <p className="mt-[3px] font-sans text-[14px] leading-[1.4] text-muted-foreground">
                                  {repo.description}
                                </p>
                              )}
                            </div>
                            <span className="font-mono text-[12px] font-semibold text-muted-foreground">
                              ★ {repo.stars}
                            </span>
                            {repo.language && (
                              <span
                                className={cn(
                                  'rounded-[5px] px-[7px] py-[3px] font-mono text-[11px] text-white',
                                  repo.language === 'Python' ? 'bg-[#4E9476]' : 'bg-primary',
                                )}
                              >
                                {repo.language}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {engineer.githubGhost && (
                    <GhostSection
                      variant="actionable"
                      title={GITHUB_GHOST_META[engineer.githubGhost].title}
                      hint={GITHUB_GHOST_META[engineer.githubGhost].hint}
                      className="sm:col-span-2"
                    />
                  )}

                  {/* Research */}
                  {research && (
                    <div className="rounded-[13px] border border-border bg-card p-[22px]">
                      <div className="mb-4 flex items-center justify-between">
                        <span className="font-heading text-[18px] font-bold text-foreground">
                          Research
                        </span>
                        <span className="font-mono text-[14px] text-muted-foreground">
                          arXiv · {research.totalCitations} citations
                        </span>
                      </div>
                      <div className="flex flex-col gap-3">
                        {research.papers.map((paper, idx) => (
                          <div
                            key={paper.title}
                            className={cn('pb-3', idx < research.papers.length - 1 && 'border-b border-border')}
                          >
                            <p className="font-sans text-[16px] font-semibold leading-[1.4] text-foreground">
                              {paper.title}
                            </p>
                            <p className="mt-[5px] font-mono text-[11px] leading-[1.4] text-muted-foreground">
                              {paper.venue} · {paper.citations} citations
                              {paper.authorPosition && ` · ${paper.authorPosition}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Models */}
                  {models && (
                    <div className="rounded-[13px] border border-border bg-card p-[22px]">
                      <div className="mb-[18px] flex items-center justify-between">
                        <div className="flex items-center gap-[9px]">
                          <span className="font-heading text-[18px] font-bold text-foreground">
                            Models
                          </span>
                          <span className="font-mono text-[14px] text-muted-foreground">
                            @{models.handle}
                          </span>
                        </div>
                        <span className={cn('font-mono text-[14px]', models.verified ? 'text-primary' : 'text-muted-foreground')}>
                          {models.verified ? '✓ verified' : '~ claimed'}
                        </span>
                      </div>
                      {(models.lastSyncedAt ?? models.syncNote) && (
                        <div className="mb-[14px] flex flex-wrap items-center gap-x-3 gap-y-1">
                          {models.lastSyncedAt && (
                            <p className="font-mono text-[11px] text-muted-foreground">
                              last synced {formatSyncedDate(models.lastSyncedAt)}
                            </p>
                          )}
                          {models.syncNote && (
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {SYNC_NOTE_MESSAGES[models.syncNote]}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="mb-5 flex gap-6">
                        <div>
                          <p className="font-heading text-[22px] font-bold text-foreground">
                            {models.followers.toLocaleString()}
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                            followers
                          </p>
                        </div>
                        <div>
                          <p className="font-heading text-[22px] font-bold text-foreground">
                            {models.totalDownloads.toLocaleString()}
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                            downloads
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-[10px]">
                        {models.models.map((model) => (
                          <div key={model.name} className="flex items-center justify-between">
                            <span className="font-mono text-[16px] font-semibold text-foreground">
                              {model.name}
                            </span>
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {model.downloads.toLocaleString()} ↓
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {engineer.huggingfaceGhost && (
                    <GhostSection
                      variant="actionable"
                      title={HUGGINGFACE_GHOST_META[engineer.huggingfaceGhost].title}
                      hint={HUGGINGFACE_GHOST_META[engineer.huggingfaceGhost].hint}
                      className="sm:col-span-2"
                    />
                  )}
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </>
  )
}
